const STORAGE_KEY = 'respondent:returnTo';

/**
 * Validates that a returnTo path is safe to redirect to (relative path only).
 * Prevents open redirect attacks by rejecting absolute URLs and protocol-relative URLs.
 */
export function getSafeReturnTo(
  returnTo: string | undefined,
): string | undefined {
  if (!returnTo) return undefined;

  // Strip ASCII control characters (0x00-0x1F, 0x7F). Browsers silently
  // remove \t, \n, \r during URL parsing; stripping all C0 controls
  // provides defense-in-depth against null-byte and other edge cases.
  const cleaned = returnTo.replaceAll(/[\u0000-\u001F\u007F]/g, '');

  // Only allow relative paths starting with /
  if (!cleaned.startsWith('/')) return undefined;

  // Block protocol-relative URLs and backslash variants (browsers normalize \ to /)
  if (/^\/[/\\]/.test(cleaned)) return undefined;
  return cleaned;
}

/**
 * Same-origin absolute URL for OAuth `url` query params. Some shared auth
 * gateways expect a full URL; passing only a path can be ignored and fall back
 * to a default (e.g. legacy researcher signup on website.*).
 *
 * Only pass paths that were already validated via {@link getSafeReturnTo} or
 * are application-controlled defaults.
 */
export function toAbsolutePath(path: string): string {
  if (typeof globalThis.location?.origin !== 'string') {
    return path;
  }
  return new URL(path, globalThis.location.origin).href;
}

/**
 * Persists a returnTo path in localStorage so it survives multi-step flows
 * like signup → verify-email → onboarding.
 */
export function storeReturnTo(returnTo: string | undefined): void {
  const safe = getSafeReturnTo(returnTo);
  try {
    if (safe) {
      globalThis.localStorage.setItem(STORAGE_KEY, safe);
    } else {
      globalThis.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // localStorage may be unavailable (SSR, private browsing, etc.)
  }
}

/**
 * Retrieves and clears the stored returnTo path from localStorage.
 * Returns undefined if nothing is stored or the value is not a safe path.
 */
export function consumeReturnTo(): string | undefined {
  try {
    const value = globalThis.localStorage.getItem(STORAGE_KEY);
    if (value) {
      globalThis.localStorage.removeItem(STORAGE_KEY);
    }
    return getSafeReturnTo(value ?? undefined);
  } catch {
    return undefined;
  }
}
